import { z } from 'zod';
import { STRAP_DESIGN_KEYS } from './strap-designs.js';
/**
 * A journalist's edit (Μοντάζ): what the timeline holds. V1 is a sequence of pieces, each a range of a source
 * (a clip of the playout library, a file the journalist imported, or a photo), one after the other with no gaps,
 * with a transition into a piece where the journalist set one. Each piece has its own look (a filter and colour
 * adjustments), its frame (scale, position, rotation, flip, crop, opacity), a slow zoom or pan across it, its speed
 * (or it holds one frame: a freeze) and its effects. V2 holds pictures laid over the sequence (a logo, a photo), T1
 * the lower thirds, T2 free texts and captions, each animated in and out. A1 (voice-over) and A2 (music) hold audio
 * clips, each a range of a voice-over take or a music bed placed at a second of the sequence, cut, trimmed and faded
 * like the pieces. A piece's own sound can be separated from its picture: it becomes a clip on A3 (the pictures'
 * sound), a range of the piece's file that moves, trims and fades on its own, and the piece turns silent. The music's dips sit at seconds of the sequence; the mix sets each track's level, mute and solo,
 * and the music's drop under the voice-over. The format sets the picture's shape (16:9, 9:16, 1:1, 4:5), what fills
 * around a piece that does not cover it, and the export's resolution and frame rate. A piece's frame, a text's and a
 * picture's place, size, turn and opacity can change over time by keyframes; a track can be hidden (it is not drawn,
 * not even in the export) or locked (nothing on it moves on the timeline). The project is what the editor
 * page saves, the browser exports, and the storyline plays; the playout gets the cut and the straps when the sequence
 * is one plain piece of the story's own clip (its own CG); any other edit is rendered for air by the playout from its
 * masters (edit-air.ts, ADR 074), or, for what the playout does not reproduce yet, the browser's export is the copy.
 */
export const STRAP_TEMPLATES = ['lower-third-1', 'lower-third-2'];
export const TRANSITION_TYPES = ['dissolve', 'dip-black', 'dip-white', 'wipe', 'push'];
/** A1 the voice-over, A2 the music, A3 the pieces' sound separated from their pictures. */
export const AUDIO_TRACKS = ['vo', 'music', 'sound'];
/** A colour look over a piece, at the strength the journalist sets. */
export const FILTERS = ['none', 'bw', 'vivid', 'warm', 'cool', 'fade', 'cinematic', 'vintage', 'noir', 'dramatic'];
/** A slow move across a piece (Ken Burns). */
export const MOTIONS = ['none', 'zoom-in', 'zoom-out', 'pan-left', 'pan-right', 'pan-up', 'pan-down'];
export const EFFECT_TYPES = ['grain', 'bars', 'flash', 'shake', 'glitch'];
export const ASPECTS = ['16:9', '9:16', '1:1', '4:5'];
export const TEXT_FONTS = ['sans', 'serif', 'condensed', 'display', 'mono'];
export const ANIMS_IN = ['none', 'fade', 'slide-up', 'slide-left', 'pop', 'typewriter', 'wipe'];
export const ANIMS_OUT = ['none', 'fade', 'slide-down', 'slide-right', 'pop', 'wipe'];
/** What a text's box is dressed with in its accent colour: a bar at its left, a line under it, its first line on a label of its own, a frame. */
export const TEXT_DECOS = ['none', 'bar', 'underline', 'tag', 'frame'];
/** The tracks of the timeline, for hiding and locking. */
export const LANES = ['overlay', 'video', 'straps', 'text', 'vo', 'music', 'sound'];
/** What changes over time by keyframes: a place (fractions of the frame), a size, a turn (degrees), an opacity. */
export const KEY_PROPS = ['x', 'y', 'scale', 'size', 'width', 'rotation', 'opacity'];
/** Which of them each kind of item has: a piece's frame, a text, a picture over the sequence. */
export const KEY_PROPS_OF = {
    segment: ['x', 'y', 'scale', 'rotation', 'opacity'],
    text: ['x', 'y', 'size', 'rotation', 'opacity'],
    overlay: ['x', 'y', 'width', 'rotation', 'opacity'],
};
/** The range each value may take in a keyframe (a text may slide out of the frame, a picture grow past it). */
export const KEY_RANGE = { x: [-1, 2], y: [-1, 2], scale: [0.05, 10], size: [0.5, 60], width: [0.01, 2], rotation: [-720, 720], opacity: [0, 1] };
export const MAX_KEYS = 60;
/** A frame at 25 fps: the shortest piece. */
export const MIN_SEGMENT_MS = 40;
/** The shortest audio clip. */
export const MIN_AUDIO_MS = 100;
export const MIN_DIP_MS = 500;
export const MIN_TRANSITION_MS = 200;
export const MAX_TRANSITION_MS = 4000;
export const MIN_ITEM_MS = 200;
export const DEFAULT_STRAP_MS = 7000;
export const DEFAULT_TRANSITION_MS = 600;
export const DEFAULT_TEXT_MS = 4000;
export const DEFAULT_OVERLAY_MS = 5000;
export const DEFAULT_PHOTO_MS = 5000;
export const DEFAULT_FREEZE_MS = 2000;
export const MAX_SEGMENTS = 100;
export const MAX_AUDIO_CLIPS = 100;
export const MAX_TEXTS = 120;
export const MAX_OVERLAYS = 40;
export const MAX_EFFECTS = 5;
/** A level above 1 boosts (up to +6 dB). */
export const MAX_GAIN = 2;
export const MIN_SPEED = 0.25;
export const MAX_SPEED = 4;
/** The longest a frozen frame or a photo holds. */
export const MAX_FREEZE_MS = 60_000;
export const MAX_PHOTO_MS = 600_000;
const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/);
const unit = (min, max, d) => z.number().min(min).max(max).default(d);
export const editSourceSchema = z.object({
    /** `clip` = the playout library (media asset id); `upload` = a video imported into the story; `image` = a photo imported into the story. */
    kind: z.enum(['clip', 'upload', 'image']),
    id: z.string().min(1).max(200),
});
export const editTransitionSchema = z.object({
    type: z.enum(TRANSITION_TYPES),
    /** Centred on the cut: half before, half after. */
    durationMs: z.number().int().min(MIN_TRANSITION_MS).max(MAX_TRANSITION_MS),
});
/** The colour of a piece: a filter at a strength, then the journalist's own adjustments (each −1..1, 0 = as shot). */
export const editLookSchema = z.object({
    filter: z.enum(FILTERS).default('none'),
    amount: unit(0, 1, 1),
    brightness: unit(-1, 1, 0),
    contrast: unit(-1, 1, 0),
    saturation: unit(-1, 1, 0),
    /** Warmer (positive) or cooler (negative). */
    temperature: unit(-1, 1, 0),
    vignette: unit(0, 1, 0),
    blur: unit(0, 1, 0),
});
export const editCropSchema = z.object({ left: unit(0, 0.45, 0), top: unit(0, 0.45, 0), right: unit(0, 0.45, 0), bottom: unit(0, 0.45, 0) });
/** Where a piece sits in the frame: its size over the fit, its centre's shift (fractions of the frame), its turn, its mirror, its crop. */
export const editTransformSchema = z.object({
    scale: unit(0.1, 5, 1),
    x: unit(-1, 1, 0),
    y: unit(-1, 1, 0),
    rotation: unit(-180, 180, 0),
    flipH: z.boolean().default(false),
    flipV: z.boolean().default(false),
    opacity: unit(0, 1, 1),
    crop: editCropSchema.default(() => ({ left: 0, top: 0, right: 0, bottom: 0 })),
});
export const editMotionSchema = z.object({ type: z.enum(MOTIONS).default('none'), amount: unit(0.02, 0.6, 0.15) });
export const editEffectSchema = z.object({ type: z.enum(EFFECT_TYPES), amount: unit(0, 1, 0.5) });
/** A value at a moment of an item: `t` in milliseconds from the item's start. */
export const editKeySchema = z.object({ t: z.number().int().min(0).max(3_600_000), v: z.number() });
const keyList = z.array(editKeySchema).max(MAX_KEYS).optional();
/** An item's keyframes, per value; a value with none keeps the item's own setting. */
export const editKeysSchema = z.object({ x: keyList, y: keyList, scale: keyList, size: keyList, width: keyList, rotation: keyList, opacity: keyList });
export const editLanesSchema = z.object({ hidden: z.array(z.enum(LANES)).max(LANES.length).default(() => []), locked: z.array(z.enum(LANES)).max(LANES.length).default(() => []) });
export const editSegmentSchema = z.object({
    id: z.string().min(1).max(40),
    source: editSourceSchema,
    /** The range of the source, in milliseconds of the source (a photo: its length on the sequence; a freeze: the frame, and how long it holds). */
    inMs: z.number().int().min(0),
    outMs: z.number().int().min(0),
    /** The piece's own sound, 0..2. */
    gain: z.number().min(0).max(MAX_GAIN).default(1),
    /** The transition from the piece before (on the first piece: from black or white). */
    transitionIn: editTransitionSchema.nullable().default(null),
    /** 2 plays the range in half the time; its sound follows (pitch with it). */
    speed: unit(MIN_SPEED, MAX_SPEED, 1),
    /** The piece holds the frame at `inMs` for `outMs − inMs`, silent. */
    freeze: z.boolean().default(false),
    look: editLookSchema.default(() => defaultLook()),
    transform: editTransformSchema.default(() => defaultTransform()),
    motion: editMotionSchema.default(() => ({ type: 'none', amount: 0.15 })),
    effects: z.array(editEffectSchema).max(MAX_EFFECTS).default(() => []),
    /** Its frame over time (x, y, scale, rotation, opacity). */
    keys: editKeysSchema.default(() => ({})),
    /** Its sound was separated: the piece is silent and its sound plays from the A3 clips of this link. */
    audioLink: z.string().min(1).max(40).nullable().default(null),
});
export const editStrapSchema = z.object({
    id: z.string().min(1).max(40),
    /** Milliseconds of the sequence. */
    atMs: z.number().int().min(0),
    durationMs: z.number().int().min(500).max(600_000),
    template: z.enum(STRAP_TEMPLATES),
    lines: z.array(z.string().max(200)).min(1).max(2),
    /** How it comes in and goes out, and how long that takes (ADR 089); a strap saved before animations cuts in and out. */
    animIn: z.enum(ANIMS_IN).optional(),
    animOut: z.enum(ANIMS_OUT).optional(),
    animMs: z.number().int().min(0).max(3000).optional(),
});
/** How a text looks: size in % of the frame's short side, its centre at (x, y) as fractions of the frame, wrapped at `width` of it. */
export const editTextStyleSchema = z.object({
    font: z.enum(TEXT_FONTS).default('sans'),
    size: unit(1, 30, 6),
    bold: z.boolean().default(true),
    italic: z.boolean().default(false),
    uppercase: z.boolean().default(false),
    color: hex.default('#ffffff'),
    /** A box behind the words, or none. */
    bg: hex.nullable().default(null),
    bgOpacity: unit(0, 1, 0.75),
    outline: z.boolean().default(false),
    shadow: z.boolean().default(true),
    align: z.enum(['left', 'center', 'right']).default('center'),
    x: unit(0, 1, 0.5),
    y: unit(0, 1, 0.5),
    width: unit(0.1, 1, 0.8),
    rotation: unit(-180, 180, 0),
    opacity: unit(0, 1, 1),
    /** The decoration's colour (none: no decoration drawn). */
    accent: hex.nullable().default(null),
    deco: z.enum(TEXT_DECOS).default('none'),
    /** The lines after the first at this share of the size, not bold (a name and a role). */
    subScale: unit(0.3, 1, 1),
});
export const editTextSchema = z.object({
    id: z.string().min(1).max(40),
    atMs: z.number().int().min(0),
    durationMs: z.number().int().min(MIN_ITEM_MS).max(600_000),
    text: z.string().max(500),
    style: editTextStyleSchema.default(() => defaultTextStyle()),
    animIn: z.enum(ANIMS_IN).default('fade'),
    animOut: z.enum(ANIMS_OUT).default('fade'),
    animMs: z.number().int().min(0).max(3000).default(400),
    /** Made from a voice-over's script ("auto captions"): made again when asked. */
    caption: z.boolean().default(false),
    keys: editKeysSchema.default(() => ({})),
});
export const editImageSourceSchema = z.object({ kind: z.literal('image'), id: z.string().min(1).max(200) });
/** A picture over the sequence (a logo, a photo): its centre at (x, y), its width as a fraction of the frame's. */
export const editOverlaySchema = z.object({
    id: z.string().min(1).max(40),
    source: editImageSourceSchema,
    atMs: z.number().int().min(0),
    durationMs: z.number().int().min(MIN_ITEM_MS).max(3_600_000),
    x: unit(0, 1, 0.88),
    y: unit(0, 1, 0.12),
    width: unit(0.02, 1, 0.16),
    opacity: unit(0, 1, 1),
    rotation: unit(-180, 180, 0),
    animIn: z.enum(ANIMS_IN).default('fade'),
    animOut: z.enum(ANIMS_OUT).default('fade'),
    animMs: z.number().int().min(0).max(3000).default(300),
    keys: editKeysSchema.default(() => ({})),
});
export const editAudioSourceSchema = z.object({
    /** `voiceover` = a take of the story; `music` = a bed of the station's library; `clip`, `upload` = the sound of a video piece's file (on A3). */
    kind: z.enum(['voiceover', 'music', 'clip', 'upload']),
    id: z.string().min(1).max(80),
});
export const editAudioClipSchema = z.object({
    id: z.string().min(1).max(40),
    track: z.enum(AUDIO_TRACKS),
    source: editAudioSourceSchema,
    /** Where the clip starts on the sequence. */
    atMs: z.number().int().min(0),
    /** The range of the file, in milliseconds of the file. */
    inMs: z.number().int().min(0),
    outMs: z.number().int().min(0),
    gain: z.number().min(0).max(MAX_GAIN).default(1),
    fadeInMs: z.number().int().min(0).max(30_000).default(0),
    fadeOutMs: z.number().int().min(0).max(30_000).default(0),
    /** A piece's sound separated from it: the piece's `audioLink` (re-linking puts the sound back in the piece). */
    link: z.string().min(1).max(40).optional(),
});
/** A section of the sequence where the music drops (while someone speaks in the picture, say). */
export const editDipSchema = z.object({
    id: z.string().min(1).max(40),
    fromMs: z.number().int().min(0),
    toMs: z.number().int().min(0),
    /** 0..1 of the music's level. */
    level: z.number().min(0).max(1),
});
export const editTrackSchema = z.object({
    gain: z.number().min(0).max(MAX_GAIN).default(1),
    mute: z.boolean().default(false),
    solo: z.boolean().default(false),
});
// zod 4: a default is returned as given, so every default here is a whole, fresh object
const trackDefault = () => ({ gain: 1, mute: false, solo: false });
export const editMixSchema = z.object({
    /** The pieces' own sound. */
    video: editTrackSchema.default(trackDefault),
    vo: editTrackSchema.default(trackDefault),
    /** A3: the pieces' sound separated from their pictures. */
    sound: editTrackSchema.default(trackDefault),
    music: editTrackSchema
        .extend({
        /** Drop under the voice-over by itself, to `duckLevel` of the music's level. */
        duck: z.boolean().default(true),
        duckLevel: z.number().min(0).max(1).default(0.25),
    })
        .default(() => ({ ...trackDefault(), duck: true, duckLevel: 0.25 })),
});
export const editEndFadeSchema = z.object({
    color: z.enum(['black', 'white']),
    durationMs: z.number().int().min(MIN_TRANSITION_MS).max(MAX_TRANSITION_MS),
});
/** The picture's shape, and what fills around a piece that does not cover it (a piece is fitted inside, or covers and is cropped). */
export const editFormatSchema = z.object({
    aspect: z.enum(ASPECTS).default('16:9'),
    fit: z.enum(['contain', 'cover']).default('contain'),
    background: z.enum(['black', 'color', 'blur']).default('black'),
    color: hex.default('#000000'),
});
/** The exported file: its short side in pixels and its frame rate. */
export const editOutputSchema = z.object({
    resolution: z.union([z.literal(720), z.literal(1080)]).default(720),
    fps: z.union([z.literal(25), z.literal(30), z.literal(50)]).default(25),
});
export const editProjectSchema = z.object({
    /** A version 3 project is a version 4 one with everything new at its default. */
    version: z.union([z.literal(3), z.literal(4)]).transform(() => 4),
    segments: z.array(editSegmentSchema).min(1).max(MAX_SEGMENTS),
    straps: z.array(editStrapSchema).max(60),
    texts: z.array(editTextSchema).max(MAX_TEXTS).default(() => []),
    overlays: z.array(editOverlaySchema).max(MAX_OVERLAYS).default(() => []),
    audio: z.array(editAudioClipSchema).max(MAX_AUDIO_CLIPS).default(() => []),
    dips: z.array(editDipSchema).max(60).default(() => []),
    mix: editMixSchema.default(() => ({ video: trackDefault(), vo: trackDefault(), sound: trackDefault(), music: { ...trackDefault(), duck: true, duckLevel: 0.25 } })),
    /** The end of the sequence fades to black or white. */
    endFade: editEndFadeSchema.nullable().default(null),
    format: editFormatSchema.default(() => defaultFormat()),
    output: editOutputSchema.default(() => defaultOutput()),
    lanes: editLanesSchema.default(() => ({ hidden: [], locked: [] })),
    /** The station's lower-third design for the straps; null: the design of the story's bulletin (ADR 083). */
    strapDesign: z.enum(STRAP_DESIGN_KEYS).nullable().default(null),
});
// ── the shapes stored before (read, never written) ──
const legacyAudioSchema = z.object({ id: z.string().min(1).max(80), atMs: z.number().int().min(0), gain: z.number().min(0).max(1) });
const legacyDipSchema = editDipSchema;
const editProjectV2Schema = z.object({
    version: z.literal(2),
    segments: z.array(z.object({ id: z.string().min(1).max(40), source: editSourceSchema, inMs: z.number().int().min(0), outMs: z.number().int().min(0) })).min(1).max(MAX_SEGMENTS),
    straps: z.array(editStrapSchema).max(60),
    voiceover: legacyAudioSchema.nullable(),
    music: legacyAudioSchema.extend({ duck: z.boolean(), dips: z.array(legacyDipSchema).max(60).default([]) }).nullable(),
    sourceGain: z.number().min(0).max(1),
});
/** The first shape (28/10): one cut of one clip, everything in milliseconds of that clip. */
const editProjectV1Schema = z.object({
    version: z.literal(1),
    cut: z.object({ inMs: z.number().int().min(0), outMs: z.number().int().min(0) }),
    straps: z.array(editStrapSchema).max(60),
    voiceover: legacyAudioSchema.nullable(),
    music: legacyAudioSchema.extend({ duck: z.boolean() }).nullable(),
    sourceGain: z.number().min(0).max(1),
});
export function defaultLook() {
    return { filter: 'none', amount: 1, brightness: 0, contrast: 0, saturation: 0, temperature: 0, vignette: 0, blur: 0 };
}
export function defaultTransform() {
    return { scale: 1, x: 0, y: 0, rotation: 0, flipH: false, flipV: false, opacity: 1, crop: { left: 0, top: 0, right: 0, bottom: 0 } };
}
export function defaultTextStyle() {
    return { font: 'sans', size: 6, bold: true, italic: false, uppercase: false, color: '#ffffff', bg: null, bgOpacity: 0.75, outline: false, shadow: true, align: 'center', x: 0.5, y: 0.5, width: 0.8, rotation: 0, opacity: 1, accent: null, deco: 'none', subScale: 1 };
}
export const defaultFormat = () => ({ aspect: '16:9', fit: 'contain', background: 'black', color: '#000000' });
export const defaultOutput = () => ({ resolution: 720, fps: 25 });
export const sourceKey = (s) => `${s.kind}:${s.id}`;
export const audioSourceKey = (s) => `${s.kind}:${s.id}`;
const randomId = (prefix) => `${prefix}${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;
/** A fresh strap id (the project is small; a short random suffix is enough). */
export const strapId = () => randomId('s');
export const segmentId = () => randomId('g');
export const dipId = () => randomId('d');
export const audioClipId = () => randomId('a');
export const textId = () => randomId('x');
export const overlayId = () => randomId('o');
/** A fresh mix: every track at its level, the music dropping to a quarter under the voice-over. */
export const defaultMix = () => ({ video: { gain: 1, mute: false, solo: false }, vo: { gain: 1, mute: false, solo: false }, sound: { gain: 1, mute: false, solo: false }, music: { gain: 1, mute: false, solo: false, duck: true, duckLevel: 0.25 } });
/** The frame of a format at a short side (the monitor draws at 720, the export at its resolution); sides even, for the encoder. */
export function frameSize(format, shortSide) {
    const even = (x) => Math.round(x / 2) * 2;
    switch (format.aspect) {
        case '9:16':
            return { w: shortSide, h: even((shortSide * 16) / 9) };
        case '1:1':
            return { w: shortSide, h: shortSide };
        case '4:5':
            return { w: shortSide, h: even((shortSide * 5) / 4) };
        default:
            return { w: even((shortSide * 16) / 9), h: shortSide };
    }
}
/** A piece that shows one picture: a frozen frame, or a photo. */
export const isStill = (s) => !!s.freeze || s.source?.kind === 'image';
/** A piece's length on the sequence: its range at its speed (a still: as long as it holds). */
export const segmentLengthMs = (s) => {
    const raw = Math.max(0, s.outMs - s.inMs);
    return isStill(s) || !s.speed || s.speed === 1 ? raw : Math.max(0, Math.round(raw / s.speed));
};
/** The second of its source a piece shows `offsetMs` into it on the sequence. */
export const sourceMsAt = (s, offsetMs) => (s.freeze ? s.inMs : s.inMs + offsetMs * (isStill(s) ? 1 : (s.speed ?? 1)));
export const audioLengthMs = (c) => Math.max(0, c.outMs - c.inMs);
export const audioEndMs = (c) => c.atMs + audioLengthMs(c);
/** An audio clip that plays a video piece's file: a piece's sound separated onto A3. */
export const isPieceSound = (s) => s.kind === 'clip' || s.kind === 'upload';
/** Whether a piece is heard itself: a still never, a piece whose sound was separated only through its clips on A3. */
export const pieceSounds = (s) => !isStill(s) && !s.audioLink;
/** The edit's length on air. */
export const editDurationMs = (p) => p.segments.reduce((a, s) => a + segmentLengthMs(s), 0);
/** Where each piece starts on the sequence. */
export function segmentStarts(p) {
    let t = 0;
    return p.segments.map((s) => {
        const at = t;
        t += segmentLengthMs(s);
        return at;
    });
}
/** The piece playing at a second of the sequence (the last piece also answers for its very end). */
export function segmentAt(p, tMs) {
    if (!p.segments.length)
        return null;
    const starts = segmentStarts(p);
    const t = Math.min(Math.max(0, tMs), editDurationMs(p));
    for (let i = p.segments.length - 1; i >= 0; i--) {
        if (t >= starts[i]) {
            const segment = p.segments[i];
            const off = Math.min(t - starts[i], segmentLengthMs(segment));
            return { index: i, segment, startMs: starts[i], sourceMs: sourceMsAt(segment, off) };
        }
    }
    return null;
}
/** A piece of a source: its own sound up, no transition, as shot, at its speed. */
export const makeSegment = (source, inMs, outMs) => ({
    id: segmentId(),
    source,
    inMs,
    outMs,
    gain: 1,
    transitionIn: null,
    speed: 1,
    freeze: false,
    look: defaultLook(),
    transform: defaultTransform(),
    motion: { type: 'none', amount: 0.15 },
    effects: [],
    keys: {},
    audioLink: null,
});
/** One piece: the whole source, its own sound up, nothing laid over it. */
export function defaultProject(source, sourceDurationMs) {
    return {
        version: 4,
        segments: [makeSegment(source, 0, Math.max(MIN_SEGMENT_MS, Math.round(sourceDurationMs)))],
        straps: [],
        texts: [],
        overlays: [],
        audio: [],
        dips: [],
        mix: defaultMix(),
        endFade: null,
        format: defaultFormat(),
        output: defaultOutput(),
        lanes: { hidden: [], locked: [] },
        strapDesign: null,
    };
}
/** A text at a second of the sequence, styled as asked. */
export const makeText = (atMs, text, style = {}, extra = {}) => ({
    id: textId(),
    atMs: Math.max(0, Math.round(atMs)),
    durationMs: DEFAULT_TEXT_MS,
    text,
    style: { ...defaultTextStyle(), ...style },
    animIn: 'fade',
    animOut: 'fade',
    animMs: 400,
    caption: false,
    keys: {},
    ...extra,
});
/** A picture over the sequence: by default a small one in the top right corner (where a logo goes). */
export const makeOverlay = (imageId, atMs, durationMs = DEFAULT_OVERLAY_MS) => ({
    id: overlayId(),
    source: { kind: 'image', id: imageId },
    atMs: Math.max(0, Math.round(atMs)),
    durationMs,
    x: 0.88,
    y: 0.12,
    width: 0.16,
    opacity: 1,
    rotation: 0,
    animIn: 'fade',
    animOut: 'fade',
    animMs: 300,
    keys: {},
});
// ── keyframes ──
const easeInOut = (q) => (q < 0.5 ? 4 * q * q * q : 1 - (-2 * q + 2) ** 3 / 2);
/** A value at `t` of an item: eased between its keyframes, held before the first and after the last; `base` without any. */
export function keyValue(list, t, base) {
    if (!list || !list.length)
        return base;
    const first = list[0];
    const last = list[list.length - 1];
    if (t <= first.t)
        return first.v;
    if (t >= last.t)
        return last.v;
    for (let k = 1; k < list.length; k++) {
        const b = list[k];
        if (t <= b.t) {
            const a = list[k - 1];
            return b.t === a.t ? b.v : a.v + (b.v - a.v) * easeInOut((t - a.t) / (b.t - a.t));
        }
    }
    return last.v;
}
/** Two keyframes closer than this (a frame) are the same one. */
export const KEY_SNAP_MS = 20;
export const hasKeys = (keys, prop) => (prop ? !!keys[prop]?.length : KEY_PROPS.some((k) => !!keys[k]?.length));
/** The keyframe of a value at `t` (within a frame), or -1. */
export const keyIndexAt = (keys, prop, t) => (keys[prop] ?? []).findIndex((k) => Math.abs(k.t - t) <= KEY_SNAP_MS);
/** A keyframe of a value set at `t` (replacing one within a frame of it). */
export function setKey(keys, prop, t, v) {
    const at = Math.max(0, Math.round(t));
    const list = (keys[prop] ?? []).filter((k) => Math.abs(k.t - at) > KEY_SNAP_MS);
    return { ...keys, [prop]: [...list, { t: at, v }].sort((a, b) => a.t - b.t) };
}
export function removeKey(keys, prop, t) {
    const list = (keys[prop] ?? []).filter((k) => Math.abs(k.t - t) > KEY_SNAP_MS);
    const next = { ...keys };
    if (list.length)
        next[prop] = list;
    else
        delete next[prop];
    return next;
}
/** Every moment an item has a keyframe, in order. */
export const keyTimes = (keys) => [...new Set(KEY_PROPS.flatMap((p) => (keys[p] ?? []).map((k) => k.t)))].sort((a, b) => a - b);
/** An item's keyframes cut at `off`: each value with keyframes keeps its value at the cut on both sides. */
export function splitKeys(keys, off) {
    const first = {};
    const second = {};
    for (const prop of KEY_PROPS) {
        const list = keys[prop];
        if (!list?.length)
            continue;
        const at = keyValue(list, off, list[0].v);
        first[prop] = [...list.filter((k) => k.t < off), { t: off, v: at }];
        second[prop] = [{ t: 0, v: at }, ...list.filter((k) => k.t > off).map((k) => ({ t: k.t - off, v: k.v }))];
    }
    return [first, second];
}
/** A piece's frame at `localMs` into it, its keyframes applied. */
export function transformAt(seg, localMs) {
    const t = seg.transform;
    const k = seg.keys ?? {};
    if (!hasKeys(k))
        return t;
    return { ...t, x: keyValue(k.x, localMs, t.x), y: keyValue(k.y, localMs, t.y), scale: keyValue(k.scale, localMs, t.scale), rotation: keyValue(k.rotation, localMs, t.rotation), opacity: keyValue(k.opacity, localMs, t.opacity) };
}
/** A text's style at `localMs` into it, its keyframes applied. */
export function textStyleAt(x, localMs) {
    const s = x.style;
    const k = x.keys ?? {};
    if (!hasKeys(k))
        return s;
    return { ...s, x: keyValue(k.x, localMs, s.x), y: keyValue(k.y, localMs, s.y), size: keyValue(k.size, localMs, s.size), rotation: keyValue(k.rotation, localMs, s.rotation), opacity: keyValue(k.opacity, localMs, s.opacity) };
}
/** A picture over the sequence at `localMs` into it, its keyframes applied. */
export function overlayAt(o, localMs) {
    const k = o.keys ?? {};
    if (!hasKeys(k))
        return o;
    return { ...o, x: keyValue(k.x, localMs, o.x), y: keyValue(k.y, localMs, o.y), width: keyValue(k.width, localMs, o.width), rotation: keyValue(k.rotation, localMs, o.rotation), opacity: keyValue(k.opacity, localMs, o.opacity) };
}
/** Keyframes kept to an item's kind, length and ranges: sorted, one per moment, at most MAX_KEYS a value. */
export function clampKeys(keys, kind, lengthMs) {
    const out = {};
    for (const prop of KEY_PROPS_OF[kind]) {
        const list = keys?.[prop];
        if (!list?.length)
            continue;
        const [lo, hi] = KEY_RANGE[prop];
        const byT = new Map();
        for (const k of list)
            if (Number.isFinite(k.t) && Number.isFinite(k.v))
                byT.set(Math.min(Math.max(0, Math.round(k.t)), Math.max(0, Math.round(lengthMs))), Math.round(Math.min(hi, Math.max(lo, k.v)) * 1000) / 1000);
        const sorted = [...byT.entries()].sort((a, b) => a[0] - b[0]).slice(0, MAX_KEYS);
        if (sorted.length)
            out[prop] = sorted.map(([t, v]) => ({ t, v }));
    }
    return out;
}
/** A strap with words on it. */
export const strapHasText = (s) => s.lines.some((l) => l.trim().length > 0);
export const textHasWords = (x) => x.text.trim().length > 0;
/** The straps on screen at `tMs` of the sequence (those with words). */
/** A new lower third's animation: the bar wipes in from the left and away again, in half a second. */
export const STRAP_ANIM = { animIn: 'wipe', animOut: 'wipe', animMs: 500 };
/** A strap's animation: as chosen, a strap saved before animations cutting in and out (a typewriter is a fade for a strap). */
export function strapAnim(s) {
    return { animIn: s.animIn === 'typewriter' ? 'fade' : (s.animIn ?? 'none'), animOut: s.animOut ?? 'none', animMs: s.animMs ?? STRAP_ANIM.animMs };
}
export const strapsAt = (p, tMs) => p.straps.filter((s) => strapHasText(s) && tMs >= s.atMs && tMs < s.atMs + s.durationMs);
/** The texts on screen at `tMs` (those with words), in the order they are drawn. */
export const textsAt = (p, tMs) => p.texts.filter((x) => textHasWords(x) && tMs >= x.atMs && tMs < x.atMs + x.durationMs);
export const overlaysAt = (p, tMs) => p.overlays.filter((o) => tMs >= o.atMs && tMs < o.atMs + o.durationMs);
/** The piece under `tMs` cut in two there (nothing happens closer than a frame to either end). */
export function splitAt(p, tMs) {
    const hit = segmentAt(p, tMs);
    if (!hit)
        return p;
    const seg = hit.segment;
    const len = segmentLengthMs(seg);
    const off = Math.round(Math.min(Math.max(0, tMs), editDurationMs(p)) - hit.startMs);
    if (off < MIN_SEGMENT_MS || len - off < MIN_SEGMENT_MS)
        return p;
    let first;
    let second;
    const [k1, k2] = splitKeys(seg.keys ?? {}, off);
    if (seg.freeze) {
        // a frozen frame stays the same frame on both sides
        first = { ...seg, outMs: seg.inMs + off, keys: k1 };
        second = { ...seg, id: segmentId(), outMs: seg.inMs + (len - off), transitionIn: null, keys: k2 };
    }
    else {
        const src = Math.round(off * (isStill(seg) ? 1 : seg.speed));
        first = { ...seg, outMs: seg.inMs + src, keys: k1 };
        // the second half starts on a plain cut; it keeps the piece's level, look and frame (its keyframes from the cut on)
        second = { ...seg, id: segmentId(), inMs: seg.inMs + src, transitionIn: null, keys: k2 };
    }
    return { ...p, segments: [...p.segments.slice(0, hit.index), first, second, ...p.segments.slice(hit.index + 1)] };
}
/**
 * The frame under `tMs` held for `holdMs`: the piece is cut there and a frozen frame (silent, with the piece's look
 * and frame) goes between the halves. Nothing happens on a still.
 */
export function freezeAt(p, tMs, holdMs = DEFAULT_FREEZE_MS) {
    const hit = segmentAt(p, tMs);
    if (!hit || isStill(hit.segment))
        return { project: p, segmentId: null };
    const frame = Math.round(hit.sourceMs);
    const split = splitAt(p, tMs);
    const off = tMs - hit.startMs;
    const index = split === p && off < MIN_SEGMENT_MS ? hit.index : hit.index + 1;
    // the frame as it is seen at that moment: its keyframes resolved
    const still = { ...makeSegment(hit.segment.source, frame, frame + Math.max(MIN_SEGMENT_MS, Math.round(holdMs))), freeze: true, gain: 0, look: hit.segment.look, transform: transformAt(hit.segment, off) };
    return { project: { ...split, segments: [...split.segments.slice(0, index), still, ...split.segments.slice(index)] }, segmentId: still.id };
}
/** The audio clips of a track sounding at `tMs` (the end of a clip belongs to the next one). */
export const audioAt = (p, track, tMs) => p.audio.filter((c) => c.track === track && tMs >= c.atMs && tMs < audioEndMs(c));
/** The audio clip `id` cut in two at `tMs` of the sequence (nothing closer than MIN_AUDIO_MS to an end). */
export function splitAudioAt(p, id, tMs) {
    const i = p.audio.findIndex((c) => c.id === id);
    if (i < 0)
        return p;
    const c = p.audio[i];
    const off = Math.round(tMs - c.atMs);
    if (off < MIN_AUDIO_MS || audioLengthMs(c) - off < MIN_AUDIO_MS)
        return p;
    const first = { ...c, outMs: c.inMs + off, fadeOutMs: 0, fadeInMs: Math.min(c.fadeInMs, off) };
    const second = { ...c, id: audioClipId(), atMs: c.atMs + off, inMs: c.inMs + off, fadeInMs: 0, fadeOutMs: Math.min(c.fadeOutMs, audioLengthMs(c) - off) };
    return { ...p, audio: [...p.audio.slice(0, i), first, second, ...p.audio.slice(i + 1)] };
}
/** A text cut in two at `tMs` (each half at least MIN_ITEM_MS). */
export function splitTextAt(p, id, tMs) {
    const i = p.texts.findIndex((x) => x.id === id);
    if (i < 0)
        return p;
    const x = p.texts[i];
    const off = Math.round(tMs - x.atMs);
    if (off < MIN_ITEM_MS || x.durationMs - off < MIN_ITEM_MS)
        return p;
    const [k1, k2] = splitKeys(x.keys ?? {}, off);
    const first = { ...x, durationMs: off, animOut: 'none', keys: k1 };
    const second = { ...x, id: textId(), atMs: x.atMs + off, durationMs: x.durationMs - off, animIn: 'none', keys: k2 };
    return { ...p, texts: [...p.texts.slice(0, i), first, second, ...p.texts.slice(i + 1)] };
}
/** Every track cut at `tMs`: the piece under the playhead and every audio clip sounding there. */
export function splitAllAt(p, tMs) {
    let next = splitAt(p, tMs);
    for (const c of p.audio)
        if (tMs > c.atMs && tMs < audioEndMs(c))
            next = splitAudioAt(next, c.id, tMs);
    return next;
}
/** A fresh link between a piece and its separated sound. */
export const audioLinkId = () => randomId('l');
/**
 * A piece's sound separated from its picture: the piece turns silent and its sound becomes a clip on A3 at the same
 * place, the same range of the same file at the piece's level, to move, trim, cut, fade or remove on its own. Only a
 * piece that has sound (not a photo or a frozen frame), at its own speed, not separated already; null otherwise.
 */
export function detachSound(p, segId) {
    const i = p.segments.findIndex((s) => s.id === segId);
    const s = p.segments[i];
    if (!s || !pieceSounds(s) || s.speed !== 1 || s.source.kind === 'image' || p.audio.length >= MAX_AUDIO_CLIPS)
        return null;
    const link = audioLinkId();
    const clip = { id: audioClipId(), track: 'sound', source: { kind: s.source.kind, id: s.source.id }, atMs: segmentStarts(p)[i], inMs: s.inMs, outMs: s.outMs, gain: s.gain, fadeInMs: 0, fadeOutMs: 0, link };
    return { project: { ...p, segments: p.segments.map((x) => (x.id === segId ? { ...x, audioLink: link } : x)), audio: [...p.audio, clip] }, clipId: clip.id };
}
/** A separated sound back in its picture: every A3 clip of the link gone (cut or moved, all of it), every piece of the link heard again. */
export function relinkSound(p, link) {
    return { ...p, segments: p.segments.map((s) => (s.audioLink === link ? { ...s, audioLink: null } : s)), audio: p.audio.filter((c) => c.link !== link) };
}
/** The piece gone; the last piece stays (a sequence is never empty). */
export function removeSegment(p, id) {
    if (p.segments.length <= 1)
        return p;
    return { ...p, segments: p.segments.filter((s) => s.id !== id) };
}
/** The piece at another place in the order. */
export function moveSegment(p, id, toIndex) {
    const from = p.segments.findIndex((s) => s.id === id);
    if (from < 0)
        return p;
    const next = [...p.segments];
    const [seg] = next.splice(from, 1);
    next.splice(Math.min(Math.max(0, toIndex), next.length), 0, seg);
    return { ...p, segments: next };
}
/** The boundary (0 = the start, n = the end) nearest to `atMs` of the sequence. */
export function nearestBoundary(p, atMs) {
    const bounds = [...segmentStarts(p), editDurationMs(p)];
    let index = bounds.length - 1;
    let best = Infinity;
    bounds.forEach((b, i) => {
        const d = Math.abs(b - atMs);
        if (d < best) {
            best = d;
            index = i;
        }
    });
    return index;
}
/** A new piece in the order, at the boundary nearest to `atMs` of the sequence (the end when beyond it). */
export function insertSegment(p, segment, atMs) {
    const index = nearestBoundary(p, atMs);
    return { ...p, segments: [...p.segments.slice(0, index), segment, ...p.segments.slice(index)] };
}
/** A transition into the piece after the boundary nearest to `atMs` (the first piece for the start); null takes it away. */
export function setTransitionNear(p, atMs, transition) {
    const b = Math.min(nearestBoundary(p, atMs), p.segments.length - 1);
    const seg = p.segments[b];
    if (!seg)
        return { project: p, segmentId: null };
    // at the start there is nothing to dissolve from: the first piece comes out of black (or white)
    const tr = transition && b === 0 && transition.type !== 'dip-white' ? { ...transition, type: 'dip-black' } : transition;
    return { project: { ...p, segments: p.segments.map((s, i) => (i === b ? { ...s, transitionIn: tr } : s)) }, segmentId: seg.id };
}
/** A look that changes nothing. */
export const lookIsPlain = (l) => (l.filter === 'none' || l.amount === 0) && !l.brightness && !l.contrast && !l.saturation && !l.temperature && !l.vignette && !l.blur;
export const transformIsPlain = (t) => t.scale === 1 && !t.x && !t.y && !t.rotation && !t.flipH && !t.flipV && t.opacity === 1 && !t.crop.left && !t.crop.top && !t.crop.right && !t.crop.bottom;
/** A piece shown as shot: its own speed, no look, frame, move or effect. */
export const pieceIsPlain = (s) => s.speed === 1 && !s.freeze && lookIsPlain(s.look) && transformIsPlain(s.transform) && s.motion.type === 'none' && !s.effects.length && !hasKeys(s.keys ?? {});
/**
 * The edit is one plain piece of the story's own clip, in 16:9, with nothing over it but straps: the playout can
 * play the cut with its own CG. Anything else needs the exported file.
 */
export function isPlainCut(p, clipId) {
    const s = p.segments[0];
    return p.segments.length === 1 && !!s && s.source.kind === 'clip' && s.source.id === clipId && pieceIsPlain(s) && !s.audioLink && !p.audio.some((c) => c.track === 'sound') && !p.texts.some(textHasWords) && !p.overlays.length && p.format.aspect === '16:9' && !(p.lanes?.hidden ?? []).length;
}
// ── captions from a voice-over's script ──
/** A script as caption lines: at the ends of sentences first, then between words, each at most `maxChars`. */
export function captionChunks(text, maxChars = 60) {
    const out = [];
    const sentences = text
        .replace(/\s+/g, ' ')
        .trim()
        .split(/(?<=[.!;?…·:])\s+/)
        .filter(Boolean);
    for (const s of sentences) {
        if (s.length <= maxChars) {
            out.push(s);
            continue;
        }
        // a long sentence: words into lines of about even length
        const words = s.split(' ');
        const parts = Math.ceil(s.length / maxChars);
        const target = Math.ceil(s.length / parts);
        let line = '';
        for (const w of words) {
            if (line && (line.length + 1 + w.length > maxChars || line.length >= target)) {
                out.push(line);
                line = w;
            }
            else
                line = line ? `${line} ${w}` : w;
        }
        if (line)
            out.push(line);
    }
    return out;
}
/**
 * Captions for an audio clip of a take: the script's lines spread over the whole take by their length (a pause after
 * each sentence), the ones the clip plays placed on the sequence. Timed by the text, not by speech recognition.
 */
export function captionsForClip(clip, script, takeMs) {
    const chunks = captionChunks(script);
    if (!chunks.length || takeMs <= 0)
        return [];
    const weights = chunks.map((c) => c.length + (/[.!;?…·:]$/.test(c) ? 8 : 2));
    const total = weights.reduce((a, b) => a + b, 0);
    const out = [];
    let acc = 0;
    chunks.forEach((text, i) => {
        const a = (acc / total) * takeMs;
        acc += weights[i];
        const b = (acc / total) * takeMs;
        const from = Math.max(a, clip.inMs);
        const to = Math.min(b, clip.outMs);
        if (to - from < 300)
            return;
        out.push({ atMs: Math.round(clip.atMs + (from - clip.inMs)), durationMs: Math.round(to - from), text });
    });
    return out;
}
const noDurations = { video: () => null, audio: () => null };
const clampTo = (x, a, b, d) => (typeof x === 'number' && Number.isFinite(x) ? Math.min(b, Math.max(a, x)) : d);
const clamp01 = (x, max = 1) => Math.min(max, Math.max(0, x));
const round3 = (x) => Math.round(x * 1000) / 1000;
const track = (t) => ({ gain: clamp01(t?.gain ?? 1, MAX_GAIN), mute: !!t?.mute, solo: !!t?.solo });
function clampLook(l) {
    const d = defaultLook();
    return {
        filter: l?.filter && FILTERS.includes(l.filter) ? l.filter : 'none',
        amount: round3(clampTo(l?.amount, 0, 1, d.amount)),
        brightness: round3(clampTo(l?.brightness, -1, 1, 0)),
        contrast: round3(clampTo(l?.contrast, -1, 1, 0)),
        saturation: round3(clampTo(l?.saturation, -1, 1, 0)),
        temperature: round3(clampTo(l?.temperature, -1, 1, 0)),
        vignette: round3(clampTo(l?.vignette, 0, 1, 0)),
        blur: round3(clampTo(l?.blur, 0, 1, 0)),
    };
}
function clampTransform(t) {
    const c = t?.crop;
    return {
        scale: round3(clampTo(t?.scale, 0.1, 5, 1)),
        x: round3(clampTo(t?.x, -1, 1, 0)),
        y: round3(clampTo(t?.y, -1, 1, 0)),
        rotation: round3(clampTo(t?.rotation, -180, 180, 0)),
        flipH: !!t?.flipH,
        flipV: !!t?.flipV,
        opacity: round3(clampTo(t?.opacity, 0, 1, 1)),
        // the crop leaves at least a tenth of the picture each way
        crop: (() => {
            const left = clampTo(c?.left, 0, 0.45, 0);
            const top = clampTo(c?.top, 0, 0.45, 0);
            return { left: round3(left), top: round3(top), right: round3(Math.min(clampTo(c?.right, 0, 0.45, 0), 0.9 - left)), bottom: round3(Math.min(clampTo(c?.bottom, 0, 0.45, 0), 0.9 - top)) };
        })(),
    };
}
function clampTextStyle(s) {
    const d = defaultTextStyle();
    const isHex = (x) => typeof x === 'string' && /^#[0-9a-fA-F]{6}$/.test(x);
    return {
        font: s?.font && TEXT_FONTS.includes(s.font) ? s.font : d.font,
        size: round3(clampTo(s?.size, 1, 30, d.size)),
        bold: s?.bold ?? d.bold,
        italic: !!s?.italic,
        uppercase: !!s?.uppercase,
        color: isHex(s?.color) ? s.color : d.color,
        bg: isHex(s?.bg) ? s.bg : null,
        bgOpacity: round3(clampTo(s?.bgOpacity, 0, 1, d.bgOpacity)),
        outline: !!s?.outline,
        shadow: s?.shadow ?? d.shadow,
        align: s?.align === 'left' || s?.align === 'right' ? s.align : 'center',
        x: round3(clampTo(s?.x, 0, 1, d.x)),
        y: round3(clampTo(s?.y, 0, 1, d.y)),
        width: round3(clampTo(s?.width, 0.1, 1, d.width)),
        rotation: round3(clampTo(s?.rotation, -180, 180, 0)),
        opacity: round3(clampTo(s?.opacity, 0, 1, 1)),
        accent: isHex(s?.accent) ? s.accent : null,
        deco: s?.deco && TEXT_DECOS.includes(s.deco) ? s.deco : 'none',
        subScale: round3(clampTo(s?.subScale, 0.3, 1, 1)),
    };
}
/**
 * A project made consistent with its sources: each piece inside its source and at least a frame long (a still as
 * long as it may hold), its speed, look and frame in range, each transition no longer than the pieces it joins, each
 * audio clip inside its file and at least MIN_AUDIO_MS long with its fades inside it, the straps, texts, pictures and
 * dips inside the sequence and in order of time, levels in range.
 */
export function clampProject(p, durations = noDurations) {
    const d = typeof durations === 'function' ? { video: durations, audio: () => null } : durations;
    const pieces = p.segments.slice(0, MAX_SEGMENTS).map((s) => {
        const still = isStill(s);
        const speed = still ? 1 : Math.round(clampTo(s.speed, MIN_SPEED, MAX_SPEED, 1) * 100) / 100;
        const dur = s.source.kind === 'image' ? null : d.video(s.source);
        let inMs = Math.max(0, Math.round(s.inMs));
        let outMs = Math.round(s.outMs);
        if (dur !== null) {
            const len = Math.max(MIN_SEGMENT_MS, Math.round(dur));
            inMs = Math.min(inMs, len - MIN_SEGMENT_MS);
            // a frozen frame may hold longer than its source; any other piece ends inside it
            if (!s.freeze)
                outMs = Math.min(outMs, len);
        }
        if (s.freeze)
            outMs = Math.min(outMs, inMs + MAX_FREEZE_MS);
        else if (s.source.kind === 'image')
            outMs = Math.min(outMs, inMs + MAX_PHOTO_MS);
        outMs = Math.max(outMs, inMs + Math.ceil(MIN_SEGMENT_MS * speed));
        const seen = new Set();
        const effects = (s.effects ?? [])
            .filter((e) => EFFECT_TYPES.includes(e.type) && !seen.has(e.type) && !!seen.add(e.type))
            .slice(0, MAX_EFFECTS)
            .map((e) => ({ type: e.type, amount: round3(clampTo(e.amount, 0, 1, 0.5)) }));
        return {
            ...s,
            inMs,
            outMs,
            gain: clamp01(s.gain ?? 1, MAX_GAIN),
            transitionIn: s.transitionIn ?? null,
            speed,
            freeze: !!s.freeze,
            look: clampLook(s.look),
            transform: clampTransform(s.transform),
            motion: { type: s.motion && MOTIONS.includes(s.motion.type) ? s.motion.type : 'none', amount: round3(clampTo(s.motion?.amount, 0.02, 0.6, 0.15)) },
            effects,
            keys: clampKeys(s.keys, 'segment', segmentLengthMs({ inMs, outMs, speed, freeze: !!s.freeze, source: s.source })),
            // a photo or a frozen frame has no sound to separate
            audioLink: still ? null : (s.audioLink ?? null),
        };
    });
    // a transition is centred on its cut: it may not be longer than either piece it joins (the first piece's, than itself)
    const segments = pieces.map((s, i) => {
        if (!s.transitionIn)
            return s;
        const room = Math.min(segmentLengthMs(s), i > 0 ? segmentLengthMs(pieces[i - 1]) : Infinity);
        if (room < MIN_TRANSITION_MS)
            return { ...s, transitionIn: null };
        return { ...s, transitionIn: { ...s.transitionIn, durationMs: Math.round(Math.min(Math.max(MIN_TRANSITION_MS, s.transitionIn.durationMs), MAX_TRANSITION_MS, room)) } };
    });
    const total = segments.reduce((a, s) => a + segmentLengthMs(s), 0);
    const inside = (at, keep = 500) => Math.min(Math.max(0, Math.round(at)), Math.max(0, total - keep));
    const audio = (p.audio ?? [])
        .slice(0, MAX_AUDIO_CLIPS)
        .map((c) => {
        const dur = isPieceSound(c.source) ? d.video(c.source) : d.audio(c.source);
        let inMs = Math.max(0, Math.round(c.inMs));
        let outMs = Math.round(c.outMs);
        if (dur !== null) {
            const len = Math.max(MIN_AUDIO_MS, Math.round(dur));
            inMs = Math.min(inMs, len - MIN_AUDIO_MS);
            outMs = Math.min(outMs, len);
        }
        outMs = Math.max(outMs, inMs + MIN_AUDIO_MS);
        const len = outMs - inMs;
        const fadeInMs = Math.min(Math.max(0, Math.round(c.fadeInMs ?? 0)), len);
        const fadeOutMs = Math.min(Math.max(0, Math.round(c.fadeOutMs ?? 0)), len - fadeInMs);
        return { ...c, atMs: Math.min(Math.max(0, Math.round(c.atMs)), Math.max(0, total - MIN_AUDIO_MS)), inMs, outMs, gain: clamp01(c.gain ?? 1, MAX_GAIN), fadeInMs, fadeOutMs };
    })
        .sort((a, b) => (a.track === b.track ? a.atMs - b.atMs : AUDIO_TRACKS.indexOf(a.track) - AUDIO_TRACKS.indexOf(b.track)));
    // a dip sits inside the sequence, at least half a second long, in order of time
    const dips = (p.dips ?? [])
        .slice(0, 60)
        .map((x) => {
        const fromMs = Math.min(Math.max(0, Math.round(x.fromMs)), Math.max(0, total - MIN_DIP_MS));
        return { ...x, fromMs, toMs: Math.max(fromMs + MIN_DIP_MS, Math.min(Math.round(x.toMs), Math.max(total, fromMs + MIN_DIP_MS))), level: clamp01(x.level) };
    })
        .sort((a, b) => a.fromMs - b.fromMs);
    // texts and pictures: inside the sequence, at least MIN_ITEM_MS (an animation longer than half of one is shortened when drawn)
    const texts = (p.texts ?? [])
        .slice(0, MAX_TEXTS)
        .map((x) => {
        const durationMs = Math.min(600_000, Math.max(MIN_ITEM_MS, Math.round(x.durationMs)));
        return {
            ...x,
            atMs: inside(x.atMs, MIN_ITEM_MS),
            durationMs,
            text: x.text.slice(0, 500),
            style: clampTextStyle(x.style),
            animIn: ANIMS_IN.includes(x.animIn) ? x.animIn : 'fade',
            animOut: ANIMS_OUT.includes(x.animOut) ? x.animOut : 'fade',
            animMs: Math.min(Math.max(0, Math.round(x.animMs ?? 400)), 3000),
            caption: !!x.caption,
            keys: clampKeys(x.keys, 'text', durationMs),
        };
    })
        .sort((a, b) => a.atMs - b.atMs);
    const overlays = (p.overlays ?? [])
        .slice(0, MAX_OVERLAYS)
        .map((o) => {
        const durationMs = Math.min(3_600_000, Math.max(MIN_ITEM_MS, Math.round(o.durationMs)));
        return {
            ...o,
            atMs: inside(o.atMs, MIN_ITEM_MS),
            durationMs,
            x: round3(clampTo(o.x, 0, 1, 0.88)),
            y: round3(clampTo(o.y, 0, 1, 0.12)),
            width: round3(clampTo(o.width, 0.02, 1, 0.16)),
            opacity: round3(clampTo(o.opacity, 0, 1, 1)),
            rotation: round3(clampTo(o.rotation, -180, 180, 0)),
            animIn: ANIMS_IN.includes(o.animIn) ? o.animIn : 'fade',
            animOut: ANIMS_OUT.includes(o.animOut) ? o.animOut : 'fade',
            animMs: Math.min(Math.max(0, Math.round(o.animMs ?? 300)), 3000),
            keys: clampKeys(o.keys, 'overlay', durationMs),
        };
    })
        .sort((a, b) => a.atMs - b.atMs);
    const m = p.mix ?? defaultMix();
    const endFade = p.endFade ? (total >= MIN_TRANSITION_MS ? { ...p.endFade, durationMs: Math.round(Math.min(Math.max(MIN_TRANSITION_MS, p.endFade.durationMs), MAX_TRANSITION_MS, total)) } : null) : null;
    const f = p.format ?? defaultFormat();
    const o = p.output ?? defaultOutput();
    return {
        version: 4,
        segments,
        // the lines stay as typed (a trim here would eat the space the journalist is typing); a strap with no words yet
        // stays too, and is skipped on screen and when applied
        straps: p.straps.map((s) => ({ ...s, atMs: inside(s.atMs), durationMs: Math.min(600_000, Math.max(500, Math.round(s.durationMs))), lines: s.lines.slice(0, 2), ...(s.animMs !== undefined ? { animMs: Math.min(Math.max(0, Math.round(s.animMs)), 3000) } : {}) })).map((s) => (s.lines.length ? s : { ...s, lines: [''] })).sort((a, b) => a.atMs - b.atMs),
        texts,
        overlays,
        audio,
        dips,
        mix: { video: track(m.video), vo: track(m.vo), sound: track(m.sound), music: { ...track(m.music), duck: m.music?.duck ?? true, duckLevel: clamp01(m.music?.duckLevel ?? 0.25) } },
        endFade,
        format: {
            aspect: ASPECTS.includes(f.aspect) ? f.aspect : '16:9',
            fit: f.fit === 'cover' ? 'cover' : 'contain',
            background: f.background === 'color' || f.background === 'blur' ? f.background : 'black',
            color: /^#[0-9a-fA-F]{6}$/.test(f.color ?? '') ? f.color : '#000000',
        },
        output: { resolution: o.resolution === 1080 ? 1080 : 720, fps: o.fps === 30 || o.fps === 50 ? o.fps : 25 },
        lanes: { hidden: [...new Set((p.lanes?.hidden ?? []).filter((l) => LANES.includes(l)))], locked: [...new Set((p.lanes?.locked ?? []).filter((l) => LANES.includes(l)))] },
        strapDesign: p.strapDesign ?? null,
    };
}
/** A long default for a sound whose length is not known yet: the clamp shortens it once the file is read. */
const UNKNOWN_AUDIO_MS = 600_000;
function fromV2(v2, legacy) {
    const lengthOf = (s) => legacy.audioDurationOf?.(s) ?? UNKNOWN_AUDIO_MS;
    const audio = [];
    if (v2.voiceover) {
        const source = { kind: 'voiceover', id: v2.voiceover.id };
        audio.push({ id: audioClipId(), track: 'vo', source, atMs: v2.voiceover.atMs, inMs: 0, outMs: lengthOf(source), gain: v2.voiceover.gain, fadeInMs: 0, fadeOutMs: 0 });
    }
    const mix = defaultMix();
    mix.video.gain = v2.sourceGain;
    if (v2.music) {
        const source = { kind: 'music', id: v2.music.id };
        audio.push({ id: audioClipId(), track: 'music', source, atMs: v2.music.atMs, inMs: 0, outMs: lengthOf(source), gain: v2.music.gain, fadeInMs: 0, fadeOutMs: 0 });
        mix.music.duck = v2.music.duck;
    }
    return {
        ...defaultProject(legacy.source, legacy.durationMs),
        segments: v2.segments.map((s) => ({ ...makeSegment(s.source, s.inMs, s.outMs), id: s.id })),
        straps: v2.straps,
        audio,
        dips: v2.music?.dips ?? [],
        mix,
    };
}
/**
 * A stored project in today's shape: a v4 project as it is, a v3 one with everything new at its default; a v2
 * project (one voice-over and one music bed) gets its sounds as clips on A1 and A2 and its levels in the mix; a v1
 * project (one cut of the story's clip) becomes one piece of that clip first, with the straps and the sounds moved
 * from the clip's seconds to the sequence's.
 */
export function upgradeProject(raw, legacy) {
    const now = editProjectSchema.safeParse(raw);
    if (now.success)
        return now.data;
    const v2 = editProjectV2Schema.safeParse(raw);
    if (v2.success)
        return fromV2(v2.data, legacy);
    const v1 = editProjectV1Schema.safeParse(raw);
    if (!v1.success)
        return null;
    const { cut } = v1.data;
    const shift = (ms) => Math.max(0, ms - cut.inMs);
    return fromV2({
        version: 2,
        segments: [{ id: segmentId(), source: legacy.source, inMs: cut.inMs, outMs: Math.max(cut.inMs + MIN_SEGMENT_MS, cut.outMs || legacy.durationMs) }],
        straps: v1.data.straps.map((s) => ({ ...s, atMs: shift(s.atMs) })),
        voiceover: v1.data.voiceover ? { ...v1.data.voiceover, atMs: shift(v1.data.voiceover.atMs) } : null,
        music: v1.data.music ? { ...v1.data.music, atMs: shift(v1.data.music.atMs), dips: [] } : null,
        sourceGain: v1.data.sourceGain,
    }, legacy);
}
//# sourceMappingURL=edit.js.map